// Watch page — opens the selected video in a modal player.
//
// The iframe is built when a video is opened and torn down when the modal
// closes, so nothing is requested from the video host until a trainee asks for
// it, and the embed URL is only in the DOM while the video is playing.
//
// Cloudflare Stream videos are private, so their cards carry no embed URL at all:
// the signed player link comes back from the request that records the view.
//
// Between that, the player parameters set in WatchVideo#embed_url and the
// absence of any download control, the video is out of casual reach. It is a
// deterrent rather than DRM: browser tooling can still capture the stream, and
// the contextmenu handler below only covers our own chrome, since a right-click
// over the player itself is handled inside the cross-origin iframe.

const HEARTBEAT_MS = 30000;

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : '';
}

// Records a video being opened, and keeps the record's end time moving while
// the player stays open, for the admin user-activity screen. Every failure is
// swallowed: a trainee's video must never stop playing because a history row
// could not be written. The one thing that does depend on the response is the
// signed player link of a private video, which openVideo handles below.
function createViewTracker(viewsUrl) {
  let viewId = null;
  let videoId = null;
  let timer = null;
  // Identifies the video currently open, so a slow response for one that has
  // since been closed cannot attach its record to the next.
  let openToken = null;

  function request(method, url, body, keepalive = false) {
    return fetch(url, {
      method,
      keepalive,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: body ? JSON.stringify(body) : undefined
    });
  }

  async function open(id) {
    const response = await request('POST', viewsUrl, { watch_video_id: id });
    if (!response.ok) return null;
    return response.json();
  }

  async function beat(keepalive = false) {
    if (!viewId) return;
    const response = await request('PATCH', `${viewsUrl}/${viewId}`, null, keepalive);
    // The server stops extending a view after a long gap between heartbeats —
    // the machine slept with the player open — so carry on under a new one.
    if (response.status === 409 && timer) begin(videoId);
  }

  function begin(id) {
    const token = {};
    openToken = token;
    viewId = null;
    const opened = open(id);
    opened.then((data) => { if (data && openToken === token) viewId = data.id; }).catch(() => {});
    return opened;
  }

  // Resolves to what the server sent back for the opening, or null if it refused.
  function start(id) {
    stop();
    videoId = id;
    const opened = begin(id);
    timer = setInterval(() => beat().catch(() => {}), HEARTBEAT_MS);
    return opened;
  }

  // keepalive lets the final heartbeat finish when the tab itself is closing.
  function stop() {
    if (timer) clearInterval(timer);
    timer = null;
    beat(true).catch(() => {});
    openToken = null;
    viewId = null;
    videoId = null;
  }

  // A OneDrive video opens on Microsoft's site; only the opening is recorded.
  function recordExternal(id) {
    request('POST', viewsUrl, { watch_video_id: id }, true).catch(() => {});
  }

  return { start, stop, recordExternal };
}

function showPlayerError(frame) {
  if (!frame.isConnected) return;

  const note = document.createElement('p');
  note.className = 'watch-player-error';
  note.textContent = 'This video could not be loaded. Close it and try again.';
  frame.replaceWith(note);
}

// Points a frame at the signed link for a private video once the server has
// sent it. The modal may have been closed, or another video opened, by then.
function loadSignedPlayer(frame, opened) {
  opened
    .then((data) => {
      if (!frame.isConnected) return;
      if (data && data.embed_url) frame.src = data.embed_url;
      else showPlayerError(frame);
    })
    .catch(() => showPlayerError(frame));
}

function initWatch() {
  const modal = document.getElementById('watch-modal');
  const player = document.getElementById('watch-player');
  if (!modal || !player) return;

  const closeBtn = document.getElementById('watch-modal-close');
  const title = document.getElementById('watch-modal-title');
  const category = document.getElementById('watch-modal-category');
  const source = document.getElementById('watch-modal-source');
  const description = document.getElementById('watch-modal-description');
  const guard = document.getElementById('watch-modal-guard');
  let lastFocused = null;
  const tracker = createViewTracker(modal.dataset.viewsUrl);

  function openVideo(item) {
    const frame = document.createElement('iframe');
    if (item.dataset.embedUrl) frame.src = item.dataset.embedUrl;
    frame.title = item.dataset.title;
    frame.className = 'watch-iframe';
    frame.setAttribute('allow', 'accelerometer; encrypted-media; picture-in-picture; fullscreen');
    frame.setAttribute('allowfullscreen', '');
    // No allow-downloads in the sandbox, and no referrer passed to the service.
    frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    player.appendChild(frame);

    title.textContent = item.dataset.title;
    category.textContent = item.dataset.category;
    source.textContent = item.dataset.source;
    description.textContent = item.dataset.description;
    // What is honest to claim differs by host, so the line comes with the video.
    guard.textContent = item.dataset.guard;

    lastFocused = item;
    modal.hidden = false;
    document.body.classList.add('watch-modal-open');
    closeBtn.focus();
    const opened = tracker.start(item.dataset.videoId);
    if (!item.dataset.embedUrl) loadSignedPlayer(frame, opened);
  }

  function closeVideo() {
    // Removing the iframe stops playback; hiding the modal on its own would
    // leave the audio running behind it.
    player.querySelectorAll('.watch-iframe, .watch-player-error').forEach((node) => node.remove());
    tracker.stop();

    modal.hidden = true;
    document.body.classList.remove('watch-modal-open');
    if (lastFocused) lastFocused.focus();
  }

  // Buttons only: OneDrive cards are plain links that leave the site, and must
  // not also try to open the player.
  document.querySelectorAll('button.watch-item').forEach((item) => {
    item.addEventListener('click', () => openVideo(item));
  });

  document.querySelectorAll('a.watch-item').forEach((item) => {
    item.addEventListener('click', () => tracker.recordExternal(item.dataset.videoId));
  });

  closeBtn.addEventListener('click', closeVideo);

  // Closing the tab with a video still open sends the last heartbeat.
  window.addEventListener('pagehide', () => {
    if (!modal.hidden) tracker.stop();
  });

  modal.addEventListener('click', (event) => {
    if (event.target === modal) closeVideo();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) closeVideo();
  });

  player.addEventListener('contextmenu', (event) => event.preventDefault());
}

document.addEventListener('DOMContentLoaded', initWatch);
